/**
 * Method used for throttling calls to a desired function, with capability to cancel before throttle time is met.
 * @param fn Original function that's intended to be throttled.
 * @param delay Amount the function should be delayed.
 * @returns Newly throttled function, and cancel function for cleanup of throttled function. E.g. const [throttled, cancel] = throttle(() => {}, 200);
 */
export const throttle = <R, A extends any[]>(
  fn: (...args: A) => R,
  delay: number
): [(...args: A) => R | undefined, () => void] => {
  let wait = false;
  let timeout: undefined | number;
  let cancelled = false;

  return [
    (...args: A) => {
      if (cancelled || wait) {
        return undefined;
      }

      const val = fn(...args);

      wait = true;

      timeout = window.setTimeout(() => {
        wait = false;
      }, delay);

      return val;
    },
    () => {
      cancelled = true;
      clearTimeout(timeout);
    },
  ];
};

/**
 * Recommended method of retrying SWR related calls that should revalidate on error.  This method targets 401 status codes, with a detail.status of 'Unauthorized'.
 * Source: https://swr.vercel.app/docs/error-handling#error-retry
 * @param callback Callback method for when the revalidation exits due to retryAmount being met, or incorrect error response.
 * @param retryAmount Amount of times to attempt retry before stopping.
 * @returns SWR onErrorRetry function.
 */
export const makeSwrUnauthorizedRetry = (
  callback: (finished: boolean) => void,
  retryAmount: number = 2
) => {
  return (error, _key, _config, revalidate, { retryCount }) => {
    if (
      (error?.statusCode !== 401 &&
        error?.details?.status?.toLowerCase() !== 'unauthorized') ||
      retryCount >= retryAmount
    ) {
      callback(true);

      return;
    }

    revalidate({ retryCount });
  };
};
